/* This example requires Tailwind CSS v2.0+ */
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useRef,
  useState,
  createContext,
  useContext,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { MenuIcon, XIcon } from "@heroicons/react/outline";
import { classNames } from "../../helpers";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import UserProfilePictureAvatar from "../avatar/UserProfilePictureAvatar";
import NestedMenuItem from "./NestedMenuItem";
import Navbar from "../navbar/Navbar";
import { useAuth } from "../../context/AuthContext";
import LogoutConfirmationModal from "../modal/LogoutConfirmationModal";
import { LearnDash } from "../../api/@types/globalTypes";

type SidebarWithHeaderProps = {
  navigation?:
    | {
        name: string;
        href: string;
        learnDash?: boolean;
        icon: JSX.Element | any;
        className?: string;
        separate?: "top" | "bottom" | any;
        counter?: string | number;
        children?: { name: string; href: string }[];
        isBrowserLink?: boolean;
        learnDashUser?: LearnDash;
      }[]
    | null;
  img?: string;
  sideHeader?: any;
  sideFooter?: any;
  langModal: boolean;
  setLangModal: Dispatch<SetStateAction<boolean>>;
  showNotificationIcon?: boolean;
};

interface WrapContainerContextInterface {
  ref?: any;
}
export const ScrollContainerRef = createContext<WrapContainerContextInterface>(
  {}
);
const LEARN_DASH_URL = process.env.REACT_APP_LEARN_DASH_BASE_URL;

export default function SidebarWithHeader({
  navigation,
  sideHeader,
  img,
  sideFooter,
  langModal,
  setLangModal,
  showNotificationIcon,
}: SidebarWithHeaderProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  const WrapContainerRef = useRef<HTMLDivElement>(null);
  const Context = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);

  const SidebarWithHeaderContext = {
    ref: WrapContainerRef,
  };
  const logoutConfirmation = (logoutStatus: boolean) => {
    if (logoutStatus) Context?.logout();
    else setShowLogoutModal(false);
  };
  return (
    <ScrollContainerRef.Provider value={SidebarWithHeaderContext}>
      <div className="flex h-screen">
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-40 flex lg:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="ltr:-translate-x-full rtl:translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="ltr:-translate-x-full rtl:translate-x-full"
            >
              <div className="relative flex flex-col flex-1 w-full max-w-xs bg-white focus:outline-none ">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 pt-2 ltr:right-0 rtl:left-0 ltr:-mr-12 rtl:-ml-12">
                    <button
                      type="button"
                      className="flex items-center justify-center w-10 h-10 ml-1 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XIcon
                        className="w-6 h-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex-1 h-0 overflow-y-auto border-t border-gray-200">
                  {sideHeader}
                  <nav aria-label="Sidebar" className="mt-5">
                    <div className="px-2 space-y-1">
                      {!!navigation &&
                        navigation.map((item) => (
                          <div key={item.name + "_lg_screens"}>
                            {item.separate === "top" && (
                              <div className="border-t border-gray-200 h-[1px]"></div>
                            )}
                            {!!item?.children && item?.children.length > 0 ? (
                              <NestedMenuItem
                                closeSideBar={() => setSidebarOpen(false)}
                                item={item}
                              />
                            ) : item?.learnDash ? (
                              LearnDashLink(item)
                            ) : (
                              <>
                                {!!item.isBrowserLink
                                  ? BrowserLink(item)
                                  : RouterLink(item, pathname, () =>
                                      setSidebarOpen(false)
                                    )}
                              </>
                            )}
                            {item.separate === "bottom" && (
                              <div className="border-t border-gray-200 h-[1px]"></div>
                            )}
                          </div>
                        ))}
                    </div>
                  </nav>
                </div>
                <div className="flex-shrink-0 p-4">{sideFooter}</div>
              </div>
            </Transition.Child>
            <div className="flex-shrink-0 w-14" aria-hidden="true">
              {/* Force sidebar to shrink to fit close icon */}
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="z-0 hidden lg:flex lg:flex-shrink-0">
          <div className="flex flex-col w-64">
            {/* Sidebar component, swap this element with another sidebar if you like */}
            <div className="flex flex-col flex-1 min-h-0 bg-gray-100 border-gray-200 ltr:border-r rtl:border-l">
              <div className="flex flex-col flex-1 pb-4 overflow-y-auto">
                <div className="px-4 text-center">{sideHeader}</div>

                <nav className="flex-1 " aria-label="Sidebar">
                  <div className="px-2 ">
                    {!!navigation &&
                      navigation.map((item) => (
                        <div key={item.name + "_lg_screens"}>
                          {item.separate === "top" && (
                            <div className="border-t border-gray-200 h-[1px]"></div>
                          )}
                          {!!item?.children && item?.children.length > 0 ? (
                            <NestedMenuItem item={item} />
                          ) : item?.learnDash ? (
                            LearnDashLink(item)
                          ) : (
                            <>
                              {!!item.isBrowserLink
                                ? BrowserLink(item)
                                : RouterLink(item, pathname, () =>
                                    setSidebarOpen(false)
                                  )}
                            </>
                          )}
                          {item.separate === "bottom" && (
                            <div className="border-t border-gray-200 h-[1px]"></div>
                          )}
                        </div>
                      ))}
                  </div>
                </nav>
              </div>
              <div className="flex-shrink-0 ">{sideFooter}</div>
            </div>
          </div>
        </div>
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <div className="hidden">
            <div className="flex items-center justify-between bg-white border-b border-gray-200 px-4 py-1.5">
              <div>
                <button
                  type="button"
                  data-cy="sidebarOpen"
                  className="inline-flex items-center justify-center w-12 h-12 -mr-3 text-gray-500 rounded-md hover:text-gray-900"
                  onClick={() => setSidebarOpen(true)}
                >
                  <span className="sr-only">Open sidebar</span>
                  <MenuIcon className="w-6 h-6" aria-hidden="true" />
                </button>
              </div>
              <div>
                <UserProfilePictureAvatar
                  profilePictureUrl={img}
                  className="w-8 h-8"
                  alt="Workflow"
                />
              </div>
            </div>
          </div>
          <div
            ref={WrapContainerRef}
            className="relative z-0 flex flex-col flex-1 h-full overflow-x-hidden overflow-y-auto"
            id="scroll_container"
          >
            <div className="block md:p-4 md:px-6">
              <Navbar
                onLanguageClick={() => setLangModal(!langModal)}
                onLogout={() => {
                  setShowLogoutModal(true);
                }}
                avatarUrl={img}
                showLanguageIcon={true}
                showLogoutIcon={true}
                showChatIcon={true}
                showNotificationIcon={true}
                onClick={() => setSidebarOpen(true)}
              />
            </div>
            <div className="flex w-full h-full">
              {!!navigation && <Outlet />}
            </div>
          </div>
        </div>
      </div>
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onClose={logoutConfirmation}
      />
    </ScrollContainerRef.Provider>
  );
}

function BrowserLink(item: any) {
  return (
    <>
      <a
        href={item.href}
        target="_blank"
        className={classNames(
          "text-gray-600 hover:bg-gray-50 hover:text-gray-800",
          !!item.className && item.className,
          "group flex items-center px-2 my-2 py-2 text-sm font-medium rounded-md "
        )}
      >
        <item.icon
          className={classNames(
            "text-gray-400 group-hover:text-gray-500",
            "ltr:mr-3 rtl:ml-3 h-6 w-6"
          )}
          aria-hidden="true"
        />
        {item.name}
      </a>
    </>
  );
}
function LearnDashLink(item: any) {
  const { learnDashUser } = item;
  return (
    <>
      <form
        action={`${LEARN_DASH_URL}/wp-login.php`}
        method="POST"
        target="_blank"
      >
        <input type="hidden" name="log" value={learnDashUser?.email} />
        <input type="hidden" name="pwd" value={learnDashUser?.password} />
        <input
          type="hidden"
          name="redirect_to"
          value={`${LEARN_DASH_URL}/ar/ld-groups/${learnDashUser?.slug}/`}
        />
        <button
          type="submit"
          className="inline-flex items-center w-full px-2 py-2 my-2 text-xs font-semibold leading-5 bg-transparent border-none rounded-md hover:bg-gray-50 text-brandblue-600 focus:outline-none focus:ring-0 focus:ring-transparent focus:ring-offset-0"
        >
          <p
            className={classNames(
              "text-gray-600 w-full  hover:text-gray-800",
              !!item.className && item.className,
              "group flex items-center   text-sm font-medium"
            )}
          >
            <item.icon
              className={classNames(
                "text-gray-400 group-hover:text-gray-500",
                "ltr:mr-3 rtl:ml-3 h-6 w-6"
              )}
              aria-hidden="true"
            />
            {item.name}
          </p>
        </button>
      </form>
    </>
  );
}

function RouterLink(
  item: any,
  currentPath: string | undefined | null,
  onClick: () => void
) {
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={classNames(
        currentPath === item.href
          ? //   item.current
            "bg-gray-200 text-gray-800"
          : "text-gray-600 hover:bg-gray-50 hover:text-gray-800",
        !!item.className && item.className,
        "group flex items-center px-2 my-2 py-2 text-sm font-medium rounded-md"
      )}
    >
      <item.icon
        className={classNames(
          // item.current
          currentPath === item.href
            ? "text-gray-500"
            : "text-gray-400 group-hover:text-gray-500",
          "ltr:mr-3 rtl:ml-3 h-6 w-6"
        )}
        aria-hidden="true"
      />
      {item.name}
      {!!item.counter && (
        <p className=" inline-flex ltr:mr-auto rtl:mr-auto items-center justify-center px-0.5 text-xs  bg-gray-200 w-8 h-5  rounded-full  ">
          {item?.counter}
        </p>
      )}
    </Link>
  );
}
